import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardActions, Box, Typography } from '@mui/material';
import { CreateAgwRestApiStageRequest, UpdateAgwRestApiStageRequest } from '@localstack/types';
import { useRoutes, useAwsEffect, useAwsGetter } from '@localstack/services';
import { ProgressButton, AgwRestApiStageForm, Breadcrumbs } from '@localstack/ui';
import { DEFAULT_API_GATEWAY_ROUTES } from '@localstack/constants';

import { ApiGatewayProps } from './types';
import { RestApiNavTabs } from './components';

export const AgwRestApiStageUpsert = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_API_GATEWAY_ROUTES,
}: ApiGatewayProps): ReactElement => {
  const { goto } = useRoutes();
  const { restApiId, stageName } = useParams<'restApiId' | 'stageName'>();

  const { createStage, updateStage, isLoading } = useAwsEffect('APIGateway', ['createStage', 'updateStage'], {
    revalidate: ['getStage', 'getStages'],
    clientOverrides,
  });

  const { data: api } = useAwsGetter('APIGateway', 'getRestApi', [{ restApiId }], { clientOverrides });
  const { data: stage } = useAwsGetter('APIGateway', 'getStage', [{ restApiId, stageName }], { clientOverrides });

  const handleCreate = async (data: CreateAgwRestApiStageRequest) => {
    await createStage(data);
    goto(routes.RESOURCES_AGW1_API_STAGES, { restApiId });
  };

  const handleUpdate = async (data: UpdateAgwRestApiStageRequest) => {
    await updateStage(data);
    goto(routes.RESOURCES_AGW1_API_STAGE, { restApiId, stageName });
  };

  return (
    <Layout
      title={
        <Box>
          <Typography variant="h4">Stage Details</Typography>
          <Breadcrumbs
            mappings={[
              ['API Gateway', () => goto(routes.RESOURCES_AGW1)],
              [api?.name, () => goto(routes.RESOURCES_AGW1_API, { restApiId })],
              [stage?.stageName, () => goto(routes.RESOURCES_AGW1_API_STAGE, { restApiId, stageName })],
              [stageName ? 'Update' : 'Create Stage', null],
            ]}
          />
        </Box>
      }
      tabs={<RestApiNavTabs restApiId={restApiId as string} routes={routes} />}
      actions={
        <ProgressButton type="submit" form="update" variant="outlined" color="primary" loading={isLoading}>
          Submit
        </ProgressButton>
      }
    >
      <Card>
        <CardContent>
          <AgwRestApiStageForm
            clientOverrides={clientOverrides}
            restApiId={restApiId as string}
            stage={stage}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            loading={isLoading}
            formId="update"
          />
        </CardContent>
        <CardActions>
          <ProgressButton type="submit" form="update" variant="contained" color="primary" loading={isLoading}>
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
